import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "Choosing a school abroad – which type fits?";
const description =
  "Comparing Swedish, international and Spanish schools on the Costa del Sol: languages, curriculum, grades, returning home and the questions that decide it.";

export const Route = createFileRoute("/en/choosing-a-school-abroad")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/choosing-a-school-abroad") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/choosing-a-school-abroad"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Choosing a school abroad"
        intro="Swedish, international or Spanish school? There is no single answer that fits every family. This guide walks through the differences, the common misunderstandings and the questions that usually decide it."
      />

      <NewcomerNote />

      <Prose>
        <h2>Start with your family's timeline</h2>
        <p>
          The most important question is rarely pedagogical but practical: how long will you stay? A
          family here for one or two years has very different needs from a family moving
          permanently. The shorter the stay, the more continuity with the Swedish system matters –
          the child is going back to a Swedish class, often mid-stage. The longer the stay, the more
          it matters that the child also puts down local roots, with Spanish as a real everyday
          language.
        </p>
        <p>
          The next question is the child's age. Younger children change environment and language
          more easily. For a pupil in secondary school or sixth form, grades, eligibility and the
          study plan weigh far heavier, and the choice should be made with the paperwork in front of
          you.
        </p>

        <h2>Swedish school</h2>
        <p>
          A Swedish curriculum, teaching in Swedish and a school culture the family recognises. That
          makes returning to Sweden predictable and keeps Swedish alive as a language of learning,
          not only a home language. With us it is combined with extended Spanish and English, so the
          child still gains the languages the area calls for. The trade-off is that the school is
          smaller than a large international campus and therefore offers a narrower range of
          options.
        </p>

        <h2>International school</h2>
        <p>
          Teaching in English, often following a British curriculum or the IB, with a very mixed
          student body. A good fit for families who move between countries or aim at universities
          abroad. What is often underestimated is how quickly Swedish loses ground when it is only
          spoken at home, and that returning to Swedish schooling may require catching up. Costs
          vary widely, and the largest schools have long waiting lists in some year groups.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>

        <h2>Spanish school</h2>
        <p>
          A state or private Spanish school gives the fastest route into the language and the local
          community, and is usually the least expensive option. It also asks the most of the child
          at the start, especially for older pupils with no Spanish. For families moving permanently
          and expecting a Spanish upper secondary path, it is often the most logical choice.
        </p>

        <h2>Common misunderstandings</h2>
        <ul>
          <li>
            "The child will pick up the language in a year anyway." True for everyday speech, but
            academic language – the kind you write exams in – takes considerably longer.
          </li>
          <li>
            "We can change schools whenever we like." Places in some year groups are limited, and
            mid-year moves are rarely simple.
          </li>
          <li>
            "Supplementary Swedish is enough." It is good support, but it does not match the Swedish
            subject taught in a Swedish school.
          </li>
        </ul>

        <h2>Questions to bring to every school visit</h2>
        <ul>
          <li>Which curriculum is followed, and who reviews the quality?</li>
          <li>How large are the classes, and what is the staff-to-pupil ratio?</li>
          <li>Which language is used in lessons, and which during breaks?</li>
          <li>What support exists for pupils who need more help?</li>
          <li>Which values guide the school, and how do they shape everyday decisions when things get difficult?</li>
          <li>How does the school maintain personal follow-up as pupil numbers grow?</li>
          <li>What is included in the fee, and what costs come on top?</li>
          <li>How does the school view a return to the home country's system?</li>
          <li>What activities are offered after the school day?</li>
        </ul>

        <h2>Examine the values in practice</h2>
        <p>
          A school's values matter as much as language, curriculum and fees, but polished words on a
          website are not enough. Notice how adults treat children, how conflicts are followed up,
          how support begins and whether staff genuinely know each pupil. Size alone is not a measure
          of quality. What matters is whether the school can grow without losing sight of each
          child's wellbeing, development and strong start in life.
        </p>

        <h2>Making the decision</h2>
        <p>
          Visit several schools, ideally on an ordinary school day. Talk to families already there,
          not only to the school. Factor in travel time – an extra half hour in the car each morning
          shapes the whole family's week. And involve your child in the conversation, especially
          from the middle years upwards.
        </p>
        <p>
          Want to see how we work? Read about{" "}
          <Link className="text-primary link-quiet" to="/en/about-the-school">
            the school
          </Link>
          , our{" "}
          <Link className="text-primary link-quiet" to="/en/grade-levels">
            grade levels
          </Link>{" "}
          and{" "}
          <Link className="text-primary link-quiet" to="/en/fees">
            fees
          </Link>
          , or book a visit.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" locale="en" />

      <GuideLinks current="/valja-skola-utomlands" locale="en" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
